/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useMemo } from 'react';
import { Button, Stack } from '@mui/material';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { handleRandomAlpha, informationSteps } from 'libs/mindUtils';
import { useAppDispatch } from 'store';
import { closeDialog } from 'store/dialog/dialogSlice';
import isEmpty from 'lodash/isEmpty';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import {
  postGuideSave,
  postInspectionIpsativeUpload,
  postInspectionSave,
  postProjectInspectionById,
  postProjectSave,
  postProjectSurveyAdditionSave,
  postProjectUserUpload,
  putIpsativeGridSave,
  putProjectSave,
} from 'store/project/projectSlice';
import { useNavigate } from 'react-router-dom';
import { IStepper4Props } from 'interface/project/projectInfo';

function FooterButtonDC(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dcInputs, onChangeDC } = useProjectInfoProvider();
  const {
    projectType,
    activeStep,
    completed,
    stepper1,
    projectId,
    inspectionId,
    stepper2,
    stepper3,
    stepper4,
    stepper2Update,
  } = dcInputs;
  const { isStep1Valid, projectName, projectStartAt, projectEndAt, compName, compAdminList, userExcel, ipsativeExcel } =
    stepper1;
  const { ...others } = stepper2;

  const totalSteps = () => informationSteps.length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const completedSteps = () => Object.keys(completed).length;
  const allStepsCompleted = () => completedSteps() === totalSteps();
  const handleContinue = () => dispatch(closeDialog());

  const handleBack = async () => {
    if (activeStep - 1 === 0 && stepper2Update) {
      await handleGridSaveAction(0);
    } else if (activeStep - 1 === 1) {
      await handleStepper3Action(1);
    } else {
      onChangeDC('activeStep', activeStep - 1);
    }
  };
  const handleNext = async () => {
    const isFinish = allStepsCompleted();
    const newActiveStep =
      isLastStep() && !isFinish ? informationSteps.findIndex((step, i) => !(i in completed)) : activeStep + 1;
    if (newActiveStep === 1 && isEmpty(projectId)) {
      await handleProjectSaveAction();
    } else if (newActiveStep === 1) {
      await handleProjectUpdateAction();
      // update
    } else if (newActiveStep === 2) {
      await handleGridSaveAction(newActiveStep);
    } else if (newActiveStep === 3) {
      await handleStepper3Action(newActiveStep);
    } else if (newActiveStep === 4) {
      await handleStepper4Action(newActiveStep);
    } else if (isLastStep()) {
      navigate('/admin/project/overview');
    } else {
      onChangeDC('activeStep', newActiveStep);
      handleContinue();
    }
  };

  const handleProjectSaveAction = () => {
    dispatch(openBackdrop({}));
    dispatch(
      postProjectSave({
        projectType,
        projectName,
        projectStartAt: (projectStartAt as any)?.format('YYYYMMDD'),
        projectEndAt: (projectEndAt as any).format('YYYYMMDD'),
        compName,
        compAdminList: compAdminList.map(obj => obj.id),
      }),
    )
      .unwrap()
      .then(response => {
        const { status, data } = response;
        console.log('data', response);
        if (status === 200) {
          onChangeDC('projectId', data.result);
          if (isEmpty(inspectionId)) {
            const tmpProjectId = data.result;
            handlePostInspectionSaveAction(tmpProjectId);
          }
          if (userExcel.file != null && userExcel.rowCount > 0 && userExcel.action) {
            handleProjectUserUploadAction(data.result);
          }
          onChangeDC('activeStep', 1);
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleProjectUpdateAction = async () => {
    dispatch(openBackdrop({}));
    await dispatch(
      putProjectSave({
        projectType,
        projectName,
        projectStartAt: (projectStartAt as any)?.format('YYYYMMDD'),
        projectEndAt: (projectEndAt as any).format('YYYYMMDD'),
        compName,
        compAdminList: compAdminList.map(obj => obj.id),
        projectId,
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          if (isEmpty(inspectionId)) {
            const tmpProjectId = projectId;
            handlePostInspectionSaveAction(tmpProjectId);
          }
          if (userExcel.file != null && userExcel.rowCount > 0 && userExcel.action) {
            handleProjectUserUploadAction(projectId);
          }
          if (!isEmpty(inspectionId) && ipsativeExcel.file != null && ipsativeExcel.rowCount > 0) {
            handleInspectionIpsativeUploadAction(inspectionId);
          }
          console.log('inspectionId', inspectionId);
          onChangeDC('activeStep', 1);
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleProjectUserUploadAction = (_projectId: string) => {
    dispatch(openBackdrop({}));
    dispatch(
      postProjectUserUpload({
        projectId: _projectId,
        file: userExcel?.file,
        action: userExcel?.action || 'new',
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          console.log('userExcelResult', response);
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handlePostInspectionSaveAction = async (_projectId: string) => {
    const randomId = handleRandomAlpha();
    await dispatch(
      postInspectionSave({
        inspectionName: `자료수집(입세티브)-${randomId}`,
        inspectionDesc: `자료수집(입세티브)-${randomId}`,
        saveFileName: 'saveFileName',
        grid: '',
      }),
    )
      .unwrap()
      .then(async response => {
        const { status, data } = response;
        console.log('result', status, data);
        if (status === 200) {
          onChangeDC('inspectionId', data.result);
          if (ipsativeExcel.file != null && ipsativeExcel.rowCount > 0) {
            await handleInspectionIpsativeUploadAction(data.result);
            await handlePostProjectInspectionByIdAction(data.result, _projectId);
          }
        }
      });
  };

  const handleInspectionIpsativeUploadAction = (_inspectionId: string) => {
    dispatch(openBackdrop({}));
    dispatch(
      postInspectionIpsativeUpload({
        inspectionId: _inspectionId,
        file: ipsativeExcel?.file,
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          console.log('ipsativeUpload Result', response);
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handlePostProjectInspectionByIdAction = (_inspectionId: string, _projectId: string) => {
    dispatch(openBackdrop({}));
    dispatch(
      postProjectInspectionById({
        projectId: _projectId,
        inspectionId: _inspectionId,
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          console.log('postProjectInspectionById', response);
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleGridSaveAction = (_activeStep: number) => {
    dispatch(openBackdrop({}));
    dispatch(
      putIpsativeGridSave({
        inspectionId,
        grid: Object.values(others).join(','),
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        console.log('data', response);
        if (status === 200) {
          onChangeDC('activeStep', _activeStep);
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleStepper3Action = (step: number) => {
    dispatch(openBackdrop({}));
    dispatch(
      postProjectSurveyAdditionSave({
        projectId,
        questions: stepper3.noAdditional ? [] : stepper3.questions,
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        console.log('response', response);
        if (status === 200) {
          onChangeDC('activeStep', step);
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleStepper4Action = (step: number) => {
    dispatch(openBackdrop({}));
    dispatch(
      postGuideSave({
        projectId: dcInputs.projectId,
        des: dcInputs.stepper4.projectDescription || '',
        agreement: dcInputs.stepper4.projectAgreement || '',
        complete: dcInputs.stepper4.completeMessage || '',
        emailTitle: dcInputs.stepper4.emailTitle || '',
        emailBody: dcInputs.stepper4.emailBody || '',
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          onChangeDC('activeStep', step);
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleCalcSum = useMemo(() => {
    return (
      Object.values(others)
        .map(obj => Number(obj))
        .reduce((acc, curr) => acc + curr, 0) || 0
    );
  }, [others]);
  const ipsativeRowCount = stepper1.ipsativeExcel?.rowCount || 0;
  const remainCount = useMemo(() => ipsativeRowCount - handleCalcSum, [handleCalcSum, ipsativeRowCount]);
  const isStep4Valid = useMemo((): boolean => {
    const requiredKeys: (keyof IStepper4Props)[] = ['projectDescription', 'projectAgreement', 'completeMessage'];
    return requiredKeys.every(key => {
      const tmpCheck = stepper4[key];
      return !isEmpty(tmpCheck);
    });
  }, [stepper4]);
  const isButtonDisabled = useMemo((): boolean => {
    if (activeStep === 0 && !isStep1Valid) {
      return true;
    }
    if (activeStep === 1 && (remainCount !== 0 || ipsativeRowCount === 0)) {
      return true;
    }
    if (activeStep === 2 && !stepper3.noAdditional && stepper3.questions.length === 0) {
      return true;
    }
    if (activeStep === 3 && !isStep4Valid) {
      return true;
    }
    return false;
  }, [
    activeStep,
    ipsativeRowCount,
    isStep1Valid,
    isStep4Valid,
    remainCount,
    stepper3.noAdditional,
    stepper3.questions.length,
  ]);

  return (
    <Stack
      direction="row"
      spacing={4}
      justifyContent="flex-end"
      sx={{
        display: 'flex',
        position: 'absolute',
        bottom: '12px',
        right: '24px',
        height: 40,
      }}
    >
      <Button
        disabled={activeStep === 0}
        onClick={handleBack}
        sx={theme => ({
          width: 100,
          backgroundColor: 'mind.grey.A0',
          color: 'text.primary',
          border: `1px solid ${theme.palette.mind.grey.disabled}`,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: 'mind.custom.greyHover',
          },
          ':disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            boxShadow: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            border: 'none',
          },
        })}
      >
        이전
      </Button>
      <Button color="primary" variant="contained" onClick={handleNext} sx={{ width: 100 }} disabled={isButtonDisabled}>
        {activeStep === 4 ? '생성완료' : '다음'}
      </Button>
    </Stack>
  );
}

export default memo(FooterButtonDC);
